/**
 * SpotlightOverlay — the unified dim + cutout layer for both tutorial
 * chapters (app tour over the React shell, gameplay over the Phaser canvas).
 *
 * Implementation (see spotlightGeometry.ts): ONE fixed full-viewport div
 * whose clip-path is an evenodd path with a rounded hole per highlighted
 * region. The painted area uses the canonical DS2 modal backdrop tone
 * (rgba(0,0,0,0.35) + 8px blur, 0.60 fallback) and blocks pointer input;
 * the holes are genuinely unpainted, so hit-testing passes through to the
 * REAL element/canvas underneath — never a transparent layer intercepting
 * spotlight clicks.
 *
 * Region kinds:
 *  - cutouts:      bright + gold ring; pointer passes through when
 *                  `interactive`, otherwise a hole-sized blocker guards the
 *                  real control against accidental activation.
 *  - passthroughs: pointer passes through but the area stays DIMMED via a
 *                  pointer-events:none tint (used for "the rest of the rack"
 *                  so wrong tiles still shake with production feedback).
 *
 * z-order: above the app shell and mobile drawer (41), below DS2Modal (100)
 * so true modals (welcome / transition / completion) render over the dim.
 */
import { useEffect, useState } from 'react';
import { DS2 } from '../../theme/designTokens';
import {
  buildCutoutPath,
  mergeOverlappingRects,
  padRect,
  type SpotRect,
} from '../../tutorial/spotlight/spotlightGeometry';

export const SPOTLIGHT_Z = 90;
const PAD = 10;
const RADIUS = 14;

const STYLE = `
.tut-spot-dim {
  position: fixed; inset: 0; z-index: ${SPOTLIGHT_Z};
  background: ${DS2.modal.overlayTint};
  -webkit-backdrop-filter: blur(${DS2.modal.blurPx}px);
  backdrop-filter: blur(${DS2.modal.blurPx}px);
  transition: clip-path 220ms ease, opacity 180ms ease;
}
@supports not ((backdrop-filter: blur(4px)) or (-webkit-backdrop-filter: blur(4px))) {
  .tut-spot-dim { background: ${DS2.modal.overlayTintFallback}; }
}
.tut-spot-ring {
  position: fixed; z-index: ${SPOTLIGHT_Z + 1}; pointer-events: none;
  border: 1.5px solid rgba(201,168,76,0.9);
  border-radius: ${RADIUS}px;
  box-shadow: 0 0 0 1px rgba(201,168,76,0.25), 0 0 18px 2px rgba(201,168,76,0.35);
  transition: left 220ms ease, top 220ms ease, width 220ms ease, height 220ms ease;
}
/* Action cue: a soft gold breath on rings whose target requires interaction —
   one gentle cycle ≈1.3s, no flashing. Reduced motion: static ring instead. */
.tut-spot-ring--breathe {
  animation: tutRingBreathe 1300ms ease-in-out infinite;
}
@keyframes tutRingBreathe {
  0%, 100% { box-shadow: 0 0 0 1px rgba(201,168,76,0.25), 0 0 14px 1px rgba(201,168,76,0.30); border-color: rgba(201,168,76,0.75); }
  50%      { box-shadow: 0 0 0 2px rgba(201,168,76,0.45), 0 0 26px 5px rgba(201,168,76,0.55); border-color: rgba(240,215,140,1); }
}
.tut-spot-tint {
  position: fixed; z-index: ${SPOTLIGHT_Z}; pointer-events: none;
  background: ${DS2.modal.overlayTint};
}
@media (prefers-reduced-motion: reduce) {
  .tut-spot-dim, .tut-spot-ring { transition: none; }
  .tut-spot-ring--breathe { animation: none; }
}
`;

export interface SpotlightOverlayProps {
  /** Bright highlighted regions (viewport coords, unpadded). */
  cutouts: SpotRect[];
  /** Dimmed-but-clickable regions (e.g. the rest of the rack). */
  passthroughs?: SpotRect[];
  /** When false, hole-sized blockers protect the real controls (app tour). */
  interactive: boolean;
  /** Scenario transitions: dim the whole screen with NO cutouts (normally
   *  zero cutouts means "render nothing" so a lost target never blanks the
   *  screen — this opts into the deliberate full dim). */
  fullDim?: boolean;
  /** Actionable steps: the rings breathe (the shared action-cue language).
   *  Informational steps keep static rings. */
  animateCues?: boolean;
  /** Active-drag escape hatch (drag lesson only, while the production drag
   *  is live): the dim stays VISIBLE but stops intercepting pointer events,
   *  so the authenticated drag can travel from the rack across the board.
   *  Restored the moment the drag ends. */
  suppressBlocking?: boolean;
}

export function SpotlightOverlay({ cutouts, passthroughs = [], interactive, fullDim = false, animateCues = false, suppressBlocking = false }: SpotlightOverlayProps) {
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const measure = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, []);

  if (cutouts.length === 0 && !fullDim) return null;

  const viewport: SpotRect = { x: 0, y: 0, w: vp.w, h: vp.h };
  const padded = cutouts.map((r) => padRect(r, PAD, viewport));
  const paddedPass = passthroughs.map((r) => padRect(r, PAD, viewport));
  // Even-odd rule: nested/overlapping holes would re-paint (and re-block)
  // their intersection — the dim only ever punches disjoint merged holes.
  const dimHoles = mergeOverlappingRects([...padded, ...paddedPass]);
  const dimPath = buildCutoutPath(viewport, dimHoles, RADIUS);

  return (
    <>
      <style>{STYLE}</style>
      {/* Blocking dim — holes are clipped out, so they neither paint nor
          capture pointer events. */}
      <div
        className="tut-spot-dim"
        data-testid="tut-spot-dim"
        style={{
          clipPath: `path(evenodd, "${dimPath}")`,
          ...(suppressBlocking ? { pointerEvents: 'none' as const } : {}),
        }}
        aria-hidden="true"
      />
      {/* Passthrough tint: visually dim, pointer-transparent, with its own
          holes wherever a bright cutout overlaps it. */}
      {paddedPass.map((r, i) => (
        <div
          key={`tint-${i}`}
          className="tut-spot-tint"
          data-testid="tut-spot-tint"
          style={{
            pointerEvents: 'none',
            left: r.x, top: r.y, width: r.w, height: r.h,
            clipPath: `path(evenodd, "${buildCutoutPath({ ...r, x: 0, y: 0 }, padded
              .filter((c) => c.x < r.x + r.w && c.x + c.w > r.x && c.y < r.y + r.h && c.y + c.h > r.y)
              .map((c) => ({ x: c.x - r.x, y: c.y - r.y, w: c.w, h: c.h })), RADIUS)}")`,
          }}
          aria-hidden="true"
        />
      ))}
      {/* Gold rings around each bright region. */}
      {padded.map((r, i) => (
        <div
          key={`ring-${i}`}
          className={`tut-spot-ring${animateCues ? ' tut-spot-ring--breathe' : ''}`}
          data-testid="tut-spot-ring"
          style={{ left: r.x, top: r.y, width: r.w, height: r.h }}
          aria-hidden="true"
        />
      ))}
      {/* Informational steps: protect the real control from accidental
          activation with a hole-sized blocker (deliberate, per-target —
          not a full-screen interceptor). */}
      {!interactive && padded.map((r, i) => (
        <div
          key={`block-${i}`}
          data-testid="tut-spot-blocker"
          style={{
            position: 'fixed', zIndex: SPOTLIGHT_Z + 2,
            left: r.x, top: r.y, width: r.w, height: r.h,
            background: 'transparent', pointerEvents: 'auto',
          }}
          aria-hidden="true"
        />
      ))}
    </>
  );
}
